/**
 * MAKAI School Platform — modular activation.
 *
 * Every school-platform capability is declared here once. Navigation, admin
 * settings and server-side guards all read this single registry, so adding a
 * module is a one-line change and scales to thousands of tenants: module state
 * is one small row per school, cached per session, and defaults to enabled so
 * a missing row never breaks an existing school.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ModuleKey =
  | "attendance"
  | "timetables"
  | "fees"
  | "calendar"
  | "homework"
  | "assignments"
  | "teacher_messaging"
  | "parent_messaging"
  | "sms_notifications"
  | "email_notifications"
  | "student_profiles"
  | "teacher_profiles"
  | "library"
  | "transport"
  | "discipline"
  | "medical";

export type ModuleGroup = "Teaching & learning" | "Operations" | "People" | "Communication";

export interface ModuleDef {
  key: ModuleKey;
  label: string;
  description: string;
  group: ModuleGroup;
  /** Routes hidden from navigation when the module is off. */
  routes: string[];
  /** Modules that must be on for this one to be useful. */
  requires?: ModuleKey[];
  /** Sensitive data — flagged in the admin UI with a privacy note. */
  sensitive?: boolean;
  defaultEnabled: boolean;
}

export const SCHOOL_MODULES: ModuleDef[] = [
  {
    key: "attendance", label: "Attendance", group: "Operations", defaultEnabled: true,
    description: "Daily and lesson registers for every stream, with absence follow-up.",
    routes: ["/teacher/attendance", "/admin/attendance"],
  },
  {
    key: "timetables", label: "Timetables", group: "Operations", defaultEnabled: true,
    description: "Class, teacher and learner timetables built from the school calendar.",
    routes: ["/teacher/timetable", "/parent/timetable", "/learner/timetable"],
  },
  {
    key: "fees", label: "Fees & billing", group: "Operations", defaultEnabled: true,
    description: "Fee structures, invoices, payments and receipts in local currency.",
    routes: ["/admin/fees", "/parent/fees", "/learner/fees"],
  },
  {
    key: "calendar", label: "School calendar", group: "Operations", defaultEnabled: true,
    description: "Terms, holidays, exams and school events shared with families.",
    routes: ["/admin/calendar", "/parent/events"],
  },
  {
    key: "homework", label: "Homework", group: "Teaching & learning", defaultEnabled: true,
    description: "Short daily practice set by teachers and tracked by parents.",
    routes: ["/parent/homework"],
  },
  {
    key: "assignments", label: "Assignments", group: "Teaching & learning", defaultEnabled: true,
    description: "Longer graded work with submissions, marking and feedback.",
    routes: ["/teacher/assignments", "/learner/assignments"],
  },
  {
    key: "teacher_messaging", label: "Teacher messaging", group: "Communication", defaultEnabled: true,
    description: "Teachers message their classes and colleagues inside Makai.",
    routes: ["/teacher/comms", "/learner/comms"],
  },
  {
    key: "parent_messaging", label: "Parent messaging", group: "Communication", defaultEnabled: true,
    description: "Two-way conversations between the school and parents.",
    routes: ["/parent/comms", "/parent/messages", "/parent/announcements"],
  },
  {
    key: "sms_notifications", label: "SMS notifications", group: "Communication", defaultEnabled: false,
    description: "Text alerts for absence, fees and urgent notices. Charges may apply.",
    routes: [],
  },
  {
    key: "email_notifications", label: "Email notifications", group: "Communication", defaultEnabled: true,
    description: "Email alerts and summaries for parents and staff.",
    routes: [],
  },
  {
    key: "student_profiles", label: "Student profiles", group: "People", defaultEnabled: true,
    description: "Learner records, guardians, documents and academic history.",
    routes: ["/admin/learners", "/admin/sis"],
  },
  {
    key: "teacher_profiles", label: "Teacher profiles", group: "People", defaultEnabled: true,
    description: "Staff records, subjects taught and class assignments.",
    routes: ["/admin/staff"],
  },
  {
    key: "library", label: "Library", group: "Teaching & learning", defaultEnabled: true,
    description: "Books, past papers, notes and video resources for learners.",
    routes: ["/learner/library", "/teacher/resources"],
  },
  {
    key: "transport", label: "Transport", group: "Operations", defaultEnabled: false,
    description: "Bus routes, pick-up points and live trip updates for parents.",
    routes: ["/parent/transport"],
  },
  {
    key: "discipline", label: "Discipline records", group: "People", defaultEnabled: false, sensitive: true,
    description: "Behaviour incidents and follow-up, visible only to authorised staff.",
    routes: ["/teacher/behaviour"],
  },
  {
    key: "medical", label: "Medical records", group: "People", defaultEnabled: false, sensitive: true,
    description: "Allergies, conditions and clinic visits. Shared only with the school nurse, admins and the learner's own guardians.",
    routes: ["/parent/medical"],
  },
];

export const MODULE_BY_KEY: Record<ModuleKey, ModuleDef> = Object.fromEntries(
  SCHOOL_MODULES.map((m) => [m.key, m]),
) as Record<ModuleKey, ModuleDef>;

/** route path -> module key (longest match wins). */
const ROUTE_MODULE: Array<[string, ModuleKey]> = SCHOOL_MODULES.flatMap((m) =>
  m.routes.map((r) => [r, m.key] as [string, ModuleKey]),
).sort((a, b) => b[0].length - a[0].length);

export function moduleForRoute(path: string): ModuleKey | null {
  const hit = ROUTE_MODULE.find(([route]) => path === route || path.startsWith(`${route}/`));
  return hit ? hit[1] : null;
}

export type ModuleState = Record<ModuleKey, boolean>;

export function defaultModuleState(): ModuleState {
  return Object.fromEntries(SCHOOL_MODULES.map((m) => [m.key, m.defaultEnabled])) as ModuleState;
}

export async function fetchModuleState(schoolId: string): Promise<ModuleState> {
  const state = defaultModuleState();
  const { data, error } = await supabase
    .from("school_modules")
    .select("module_key, enabled")
    .eq("school_id", schoolId);
  if (error) return state;
  for (const row of data ?? []) {
    if (row.module_key in state) state[row.module_key as ModuleKey] = row.enabled;
  }
  return state;
}

export async function setModuleEnabled(schoolId: string, key: ModuleKey, enabled: boolean) {
  const { error } = await supabase
    .from("school_modules")
    .upsert({ school_id: schoolId, module_key: key, enabled }, { onConflict: "school_id,module_key" });
  if (error) throw error;
}

/**
 * Reads the active school's module switches. Falls back to registry defaults
 * while loading so navigation never flickers empty.
 */
export function useSchoolModules(schoolId: string | null) {
  const [modules, setModules] = useState<ModuleState>(() => defaultModuleState());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!schoolId) { setModules(defaultModuleState()); return; }
    setLoading(true);
    setModules(await fetchModuleState(schoolId));
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { refresh(); }, [refresh]);

  const isEnabled = useCallback((key: ModuleKey) => modules[key] !== false, [modules]);
  return { modules, isEnabled, loading, refresh, setModules };
}

/**
 * Resolves the school the signed-in user belongs to, for any role:
 * staff/admin via school_members, learners via their learner record,
 * parents via their first linked child.
 */
export function useMySchoolId() {
  const [schoolId, setSchoolId] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: member } = await supabase
        .from("school_members").select("school_id")
        .eq("user_id", user.id).eq("status", "active").limit(1).maybeSingle();
      if (member?.school_id) { if (alive) setSchoolId(member.school_id); return; }
      const { data: learner } = await supabase
        .from("learners").select("school_id").eq("user_id", user.id).limit(1).maybeSingle();
      if (learner?.school_id) { if (alive) setSchoolId(learner.school_id); return; }
      const { data: link } = await supabase
        .from("parent_learner_links")
        .select("learner:learners(school_id)")
        .limit(1).maybeSingle();
      const s = (link?.learner as { school_id?: string } | null)?.school_id ?? null;
      if (alive) setSchoolId(s);
    })();
    return () => { alive = false; };
  }, []);
  return schoolId;
}
